import {
  Component,
  Input,
  OnInit,
  Output,
  EventEmitter,
  AfterViewInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { AgGridAngular } from 'ag-grid-angular';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  CellClickedEvent,
} from 'ag-grid-community';
import * as moment from 'moment';
import { customAgGridTheme } from '../../shared/ag-grid-theme.config';
import { DataService } from '../../_services/data.service';
import { ToastService } from '../../_services/toast.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-gateway-table',
  templateUrl: './gateway-table.component.html',
  styleUrls: ['./gateway-table.component.scss'],
  standalone: true,
  imports: [CommonModule, AgGridAngular],
})
export class GatewayTableComponent implements OnInit, AfterViewInit, OnChanges {
  @Input() tenantId: string;
  @Input() maxHeight: string = '400px';
  @Input() showAllColumns: boolean = false;
  @Input() enablePagination: boolean = false;
  @Input() paginationPageSize: number = 100;
  @Input() rowData: any[] = null;
  @Input() hiddenColumns: string[] = [];
  @Output() countChange = new EventEmitter<number>();
  @Output() cellValueChanged = new EventEmitter<any>();

  gateways: any[] = [];
  loading = false;
  myTheme = customAgGridTheme;
  gridApi!: GridApi;
  tenants: any[] = [];
  columnDefs: ColDef[] = [];
  private isDeletingGateway = false;

  get baseColumnDefs(): ColDef[] {
    return [
      {
        field: 'tenant.tenantId',
        headerName: 'App Tenant',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 100,
        width: 130,
        flex: 0,
        editable: this.showAllColumns,
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: (params: any) => ({
          values: this.tenants.map(t => t.name || t.tenantId),
        }),
        valueGetter: (params: any) => {
          return params.data?.tenant?.name || '';
        },
        valueSetter: (params: any) => {
          const tenantName = params.newValue;
          if (tenantName && tenantName.trim()) {
            const selectedTenant = this.tenants.find(
              t => (t.name || t.tenantId) === tenantName
            );
            if (selectedTenant) {
              params.data.tenant = selectedTenant;
              return true;
            }
          }
          return false;
        },
        cellRenderer: (params: any) => {
          const tenantName = params.data?.tenant?.name;
          return tenantName || '-';
        },
        cellClass: (params: any) => {
          return !this.showAllColumns ? 'readonly-cell' : '';
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 150,
        flex: 3,
        editable: true,
        valueSetter: (params: any) => {
          if (params.newValue !== undefined && params.newValue !== null) {
            params.data.name = params.newValue;
            return true;
          }
          return false;
        },
      },
      {
        field: 'serial',
        headerName: 'Serial',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 120,
        width: 150,
        flex: 0,
        editable: true,
        cellRenderer: (params: any) => {
          const value = params.value || '-';
          if (value === '-') {
            return value;
          }
          return `
            <div class="d-flex align-items-center justify-content-between">
              <span class="text-truncate me-2" style="max-width: calc(100% - 30px);" title="${value}">${value}</span>
              <button class="btn btn-sm btn-outline-secondary copy-btn" 
                      data-value="${value}" 
                      title="Copy Serial"
                      style="padding: 2px 6px; font-size: 10px; line-height: 1;">
                <i class="fa fa-copy"></i>
              </button>
            </div>
          `;
        },
      },
      {
        field: 'attributes.eui',
        headerName: 'EUI',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 140,
        width: 170,
        flex: 0,
        editable: false,
        cellClass: 'readonly-cell',
        cellRenderer: (params: any) => {
          const value = params.value || '-';
          if (value === '-') {
            return value;
          }
          return `
            <div class="d-flex align-items-center justify-content-between">
              <span class="text-truncate me-2" style="max-width: calc(100% - 30px);" title="${value}">${value}</span>
              <button class="btn btn-sm btn-outline-secondary copy-btn" 
                      data-value="${value}" 
                      title="Copy EUI"
                      style="padding: 2px 6px; font-size: 10px; line-height: 1;">
                <i class="fa fa-copy"></i>
              </button>
            </div>
          `;
        },
      },
      {
        field: 'identifier',
        headerName: 'ID',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 120,
        width: 150,
        flex: 0,
        editable: false,
        cellClass: 'readonly-cell',
        cellRenderer: (params: any) => {
          const value = params.value || '-';
          if (value === '-') {
            return value;
          }
          return `
            <div class="d-flex align-items-center justify-content-between">
              <span class="text-truncate me-2" style="max-width: calc(100% - 30px);" title="${value}">${value}</span>
              <button class="btn btn-sm btn-outline-secondary copy-btn" 
                      data-value="${value}" 
                      title="Copy ID"
                      style="padding: 2px 6px; font-size: 10px; line-height: 1;">
                <i class="fa fa-copy"></i>
              </button>
            </div>
          `;
        },
      },
      {
        field: 'connectivity.imei',
        headerName: 'IMEI',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 140,
        width: 170,
        flex: 0,
        editable: false,
        cellClass: 'readonly-cell',
        cellRenderer: (params: any) => {
          const value = params.value || '-';
          if (value === '-') {
            return value;
          }
          return `
            <div class="d-flex align-items-center justify-content-between">
              <span class="text-truncate me-2" style="max-width: calc(100% - 30px);" title="${value}">${value}</span>
              <button class="btn btn-sm btn-outline-secondary copy-btn" 
                      data-value="${value}" 
                      title="Copy IMEI"
                      style="padding: 2px 6px; font-size: 10px; line-height: 1;">
                <i class="fa fa-copy"></i>
              </button>
            </div>
          `;
        },
      },
      {
        field: 'notes',
        headerName: 'Notes',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 120,
        width: 150,
        flex: 0,
        editable: false,
        cellClass: 'readonly-cell',
        cellRenderer: (params: any) => {
          return params.value || '-';
        },
      },
      {
        field: 'alerts',
        headerName: 'Alerts',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: this.showAllColumns ? 80 : 80,
        width: this.showAllColumns ? 90 : 100,
        flex: 0,
        editable: false,
        cellClass: 'readonly-cell',
        cellRenderer: (params: any) => {
          const isEnabled = params.data.alerts;
          if (isEnabled) {
            return '<i class="fas fa-check text-success" title="Alerts Enabled"></i>';
          } else {
            return '<i class="fas fa-xmark text-black" title="Alerts Disabled"></i>';
          }
        },
      },
      {
        field: 'online',
        headerName: 'Status',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: this.showAllColumns ? 80 : 80,
        width: this.showAllColumns ? 90 : 100,
        flex: 0,
        editable: false,
        cellClass: 'readonly-cell',
        cellRenderer: (params: any) => {
          const status = params.value ? 'Online' : 'Offline';
          const badgeClass = params.value ? 'badge-success' : 'badge-danger';
          const tooltip = params.data?.connectivity?.lastSeen
            ? `Last seen: ${moment(params.data.connectivity.lastSeen).format(
                'DD/MM/YYYY hh:mm A'
              )}`
            : 'Offline - No recent activity';
          return `<span class="badge ${badgeClass}" title="${tooltip}">${status}</span>`;
        },
      },
      {
        field: 'connectivity.lastSeen',
        headerName: 'Last Seen',
        sortable: true,
        filter: 'agTextColumnFilter',
        minWidth: 90,
        width: 120,
        flex: 0,
        editable: false,
        cellClass: 'readonly-cell',
        cellRenderer: (params: any) => {
          if (params.value) {
            return moment(params.value).fromNow();
          }
          return '-';
        },
      },
      {
        headerName: 'Actions',
        cellRenderer: (params: any) => {
          const gateway = params.data;
          const tenant = gateway?.tenant;
          let lnsLinkButton = '';

          // Build LNS Link button if integration exists
          if (tenant?.lnsIntegration) {
            const lnsIntegration = tenant.lnsIntegration;
            const serverType = lnsIntegration.config?.LORAWAN_SERVER_TYPE;
            let url = '';

            if (serverType === 'LORIOT') {
              const serverUrl = lnsIntegration.config?.LORAWAN_SERVER_URL;
              const networkTenant = tenant.lnsNetworkTenant;
              if (serverUrl && networkTenant) {
                url = `${serverUrl}/networks/${networkTenant}/gateways/${gateway.identifier}`;
              }
            } else if (serverType === 'TTN') {
              const serverUrl = lnsIntegration.config?.LORAWAN_SERVER_URL;
              url = `${serverUrl}/console/gateways/${gateway.identifier}`;
            }

            if (url) {
              lnsLinkButton = `
                <a href="${url}" target="_blank" class="btn btn-sm btn-outline-primary" title="Open in LNS">
                  <i class="fas fa-external-link-alt"></i>
                </a>
              `;
            }
          } else {
            lnsLinkButton = `
               <a class="btn btn-sm btn-light" title="No LNS Integration" disabled>
                <i class="fas fa-xmark text-black"></i>
              </a>
            `;
          }

          return `
          <div class="btn-group" role="group">
            ${lnsLinkButton}
            <button class="btn btn-sm btn-outline-danger delete-btn" 
                    title="Delete Gateway" 
                    data-action="delete" 
                    data-gateway-id="${params.data._id}">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        `;
        },
        width: 120,
        minWidth: 120,
        flex: 0,
        sortable: false,
        filter: false,
        editable: false,
        suppressMenu: true,
        pinned: 'right',
        lockPosition: true,
        resizable: false,
        cellClass: 'readonly-cell',
      },
    ];
  }

  get extendedColumnDefs(): ColDef[] {
    // All columns are now in baseColumnDefs
    return [];
  }

  getLastSeenColor(time: any): string {
    const hours = moment().diff(moment(time), 'hours', true);
    const days = moment().diff(moment(time), 'days');
    if (!time) {
      return 'text-danger';
    }
    if (hours < 4) {
      return 'text-green';
    }
    if (days < 1) {
      return 'text-warning';
    } else {
      return 'text-danger';
    }
  }

  defaultColDef: ColDef = {
    sortable: true,
    filter: true,
    resizable: true,
    suppressAutoSize: false,
    suppressSizeToFit: false,
    wrapText: false,
    autoHeight: false,
  };

  constructor(
    private dataService: DataService,
    private toast: ToastService,
    private elementRef: ElementRef
  ) {
    this.dataService.getTenants().subscribe((res: any) => {
      if (res && res.length > 0) {
        this.tenants = res;
      }
    });
  }

  copyToClipboard(text: string): void {
    if (!text || text === '-') {
      this.toast.show('Info', 'No value to copy', 'info');
      return;
    }

    navigator.clipboard.writeText(text).then(
      () => {
        this.toast.show('Success', 'Copied to clipboard', 'success');
      },
      () => {
        this.toast.show('Error', 'Failed to copy', 'danger');
      }
    );
  }

  ngOnInit() {
    this.buildColumnDefinitions();
    if (this.rowData) {
      this.gateways = this.rowData;
      this.countChange.emit(this.gateways.length);
    } else {
      this.loadGateways();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['rowData']) {
      if (this.rowData) {
        this.gateways = this.rowData;
        this.countChange.emit(this.gateways.length);

        if (this.gridApi) {
          setTimeout(() => {
            this.autoSizeColumns();
          }, 100);
        }
      } else {
        this.gateways = [];
        this.countChange.emit(0);
      }
    }

    // Handle tenantId changes - reload gateways when tenantId changes
    if (changes['tenantId'] && !this.rowData) {
      this.loadGateways();
    }

    // Handle maxHeight changes - update host attributes
    if (changes['maxHeight']) {
      setTimeout(() => {
        this.updateHostAttributes();
      });
    }
  }

  buildColumnDefinitions() {
    this.columnDefs = this.baseColumnDefs.filter(colDef => {
      if (this.hiddenColumns.length === 0) {
        return true;
      }

      // Check if this column should be hidden
      if (colDef.field) {
        return !this.hiddenColumns.includes(colDef.field);
      }

      // For columns without field (like Actions), check headerName
      if (colDef.headerName) {
        return !this.hiddenColumns.includes(colDef.headerName);
      }

      return true;
    });
  }

  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.autoSizeColumns();
    this.setupClickHandlers();
  }

  ngAfterViewInit() {
    // Setup click handlers after view initialization
    setTimeout(() => {
      this.setupClickHandlers();
      this.updateHostAttributes();
    }, 500);
  }

  updateHostAttributes() {
    // Check if maxHeight is a fixed value (px) or percentage
    const isFixedHeight = this.maxHeight && !this.maxHeight.includes('%');
    const hostElement = this.elementRef.nativeElement as HTMLElement;

    if (isFixedHeight) {
      hostElement.setAttribute('data-max-height-type', 'fixed');
      // Set the actual height from maxHeight
      hostElement.style.height = this.maxHeight;
    } else {
      hostElement.removeAttribute('data-max-height-type');
      // Reset to CSS default for percentage heights
      hostElement.style.height = '';
    }
  }

  setupClickHandlers() {
    // Find the specific gateway table grid element
    const gridElement =
      document.querySelector('app-gateway-table ag-grid-angular') ||
      document.querySelector('ag-grid-angular[data-type="gateway"]') ||
      document.querySelector('.gateway-grid ag-grid-angular');

    if (gridElement) {
      gridElement.removeEventListener('click', this.handleGridClick);
      gridElement.addEventListener('click', this.handleGridClick);
    }
  }

  handleGridClick = (event: Event) => {
    const target = event.target as HTMLElement;
    const deleteBtn = target.closest('.delete-btn') as HTMLElement;

    if (deleteBtn) {
      const gatewayId = deleteBtn.getAttribute('data-gateway-id');
      if (gatewayId) {
        const gateway = this.gateways.find(g => g._id === gatewayId);
        if (gateway) {
          this.deleteGateway(gateway);
        }
      }
    }
  };

  deleteGateway(gateway: any) {
    if (this.isDeletingGateway) {
      return;
    }
    this.isDeletingGateway = true;
    this.dataService.checkGatewayInLNS(gateway._id).subscribe({
      next: (response: any) => {
        const lnsType =
          gateway?.tenant?.lnsIntegration?.config?.LORAWAN_SERVER_TYPE || 'LNS';
        const identifier = gateway.identifier;

        Swal.fire({
          title: `Delete ${gateway.name} (ID: ${identifier})`,
          html: `Gateway <strong>${identifier}</strong> will be deleted from <strong>${lnsType}</strong> as well as from Semvar.<br><br>Are you sure you want to delete this gateway?`,
          icon: 'info',
          showCancelButton: true,
          confirmButtonColor: '#d33',
          cancelButtonColor: '#3085d6',
          confirmButtonText: `Delete Gateway from ${lnsType} and Semvar`,
          cancelButtonText: 'Cancel',
          customClass: {
            cancelButton: 'order-1 right-gap',
            confirmButton: 'order-2',
          },
        }).then(result => {
          if (result.isConfirmed) {
            this.performDelete(gateway, false);
          } else {
            this.isDeletingGateway = false;
          }
        });
      },
      error: (error: any) => {
        const status = error?.status || error?.error?.status;
        if (status === 404 || status === 400) {
          const errorData = error.error || {};
          const lnsType =
            errorData.lnsType ||
            gateway?.tenant?.lnsIntegration?.config?.LORAWAN_SERVER_TYPE ||
            'LNS';
          const lnsNetworkTenant = errorData.lnsNetworkTenant || '';
          const identifier = errorData.identifier || gateway.identifier;
          const showLnsNetworkTenant =
            lnsNetworkTenant && lnsNetworkTenant !== 'TTNLabelNotImplemented';

          Swal.fire({
            title: `Gateway Not Found in ${lnsType}`,
            html: `Gateway <strong>${identifier}</strong> was not found in <strong>${lnsType}</strong>${
              showLnsNetworkTenant
                ? ` in network <strong>${lnsNetworkTenant}</strong>`
                : ''
            } so it cannot be deleted from there.<br><br>Do you want to continue to delete it from Semvar?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: `Delete from Semvar`,
            cancelButtonText: 'Cancel',
            customClass: {
              cancelButton: 'order-1 right-gap',
              confirmButton: 'order-2',
            },
          }).then(result => {
            if (result.isConfirmed) {
              this.performDelete(gateway, true);
            } else {
              this.isDeletingGateway = false;
            }
          });
        } else {
          const lnsType =
            gateway?.tenant?.lnsIntegration?.config?.LORAWAN_SERVER_TYPE ||
            'LNS';
          const identifier = gateway.identifier;

          Swal.fire({
            title: `Delete ${gateway.name} (ID: ${identifier})`,
            html: `Failed to check gateway in LNS. Gateway <strong>${identifier}</strong> will be deleted from <strong>${lnsType}</strong> as well as from Semvar.<br><br>Are you sure you want to delete this gateway?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: `Delete Gateway`,
            cancelButtonText: 'Cancel',
            customClass: {
              cancelButton: 'order-1 right-gap',
              confirmButton: 'order-2',
            },
          }).then(result => {
            if (result.isConfirmed) {
              this.performDelete(gateway, false);
            } else {
              this.isDeletingGateway = false;
            }
          });
        }
      },
      complete: () => {},
    });
  }

  showDeleteConfirmation(gateway: any, force: boolean) {
    Swal.fire({
      title: `Delete ${gateway.name} (ID: ${gateway.identifier})`,
      text: `Are you sure you want to delete this gateway? This action can't be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: `Delete Gateway`,
      cancelButtonText: 'Cancel',
      customClass: {
        cancelButton: 'order-1 right-gap',
        confirmButton: 'order-2',
      },
    }).then(result => {
      if (result.isConfirmed) {
        this.performDelete(gateway, force);
      }
    });
  }

  performDelete(gateway: any, force: boolean) {
    this.dataService.deleteGateway(gateway, force).subscribe({
      next: () => {
        this.isDeletingGateway = false;
        Swal.fire(
          'Deleted!',
          `Gateway "${gateway.name}" has been deleted.`,
          'success'
        );
        this.loadGateways();
      },
      error: error => {
        this.isDeletingGateway = false;
        console.error('Error deleting gateway:', error);
        Swal.fire(
          'Error!',
          'Failed to delete the gateway. Please try again.',
          'error'
        );
      },
    });
  }

  onCellClicked(event: CellClickedEvent) {
    const target = event.event?.target as HTMLElement;

    // Check if the click is on a copy button
    if (
      target &&
      (target.classList.contains('copy-btn') || target.closest('.copy-btn'))
    ) {
      const copyBtn = target.classList.contains('copy-btn')
        ? target
        : target.closest('.copy-btn');
      const value = copyBtn?.getAttribute('data-value');
      if (value) {
        this.copyToClipboard(value);
      }
      return;
    }

    // Check if the click is on the Actions column
    if (event.colDef?.headerName === 'Actions') {
      if (
        target &&
        (target.classList.contains('delete-btn') ||
          target.closest('.delete-btn'))
      ) {
        this.deleteGateway(event.data);
      }
    }
  }

  onCellValueChanged(params: any) {
    console.log('Grid cell value changed event:', params);
    console.log(
      'Field:',
      params.colDef?.field,
      'New Value:',
      params.newValue,
      'Old Value:',
      params.oldValue
    );

    if (!params.colDef?.field) {
      console.warn('No field defined for cell value changed event');
      return;
    }

    this.cellValueChanged.emit(params);
    this.updateGatewayField(
      params.data,
      params.colDef.field,
      params.newValue,
      params.oldValue
    );
  }

  autoSizeColumns() {
    if (this.gridApi) {
      // Use sizeColumnsToFit for responsive flex layout
      this.gridApi.sizeColumnsToFit();
    }
  }

  async loadGateways() {
    this.loading = true;
    try {
      const allGateways = (await this.dataService
        .getGateways()
        .toPromise()) as any[];

      if (this.tenantId) {
        this.gateways = allGateways.filter(g => {
          return g.tenant === this.tenantId || g.tenant?._id === this.tenantId;
        });
      } else {
        this.gateways = allGateways;
      }

      this.gateways.sort((a, b) => {
        const aLastSeen = a.connectivity?.lastKeepAlive
          ? new Date(a.connectivity.lastKeepAlive).getTime()
          : 0;
        const bLastSeen = b.connectivity?.lastKeepAlive
          ? new Date(b.connectivity.lastKeepAlive).getTime()
          : 0;
        return bLastSeen - aLastSeen;
      });

      this.countChange.emit(this.gateways.length);

      setTimeout(() => {
        this.autoSizeColumns();
      }, 100);
    } catch (error) {
      console.error('Error loading gateways:', error);
    } finally {
      this.loading = false;
    }
  }

  updateGatewayField(
    gateway: any,
    fieldName: string,
    newValue: string,
    oldValue?: string
  ) {
    console.log('updateGatewayField called:', {
      gateway,
      fieldName,
      newValue,
      oldValue,
    });

    if (fieldName === 'tenant.tenantId') {
      const trimmedValue = newValue?.trim();
      const previousValue = oldValue?.trim();

      console.log('Tenant update:', { trimmedValue, previousValue });

      if (trimmedValue !== previousValue) {
        const updatedGateway = { ...gateway };

        if (trimmedValue && trimmedValue.length > 0) {
          const selectedTenant = this.tenants.find(
            t => t.tenantId === trimmedValue
          );
          if (selectedTenant) {
            updatedGateway.tenant = selectedTenant._id;
            console.log('Updating gateway with tenant ID:', selectedTenant._id);
          }
        } else {
          updatedGateway.tenant = null;
          console.log('Removing tenant from gateway');
        }

        console.log('Updating gateway with:', updatedGateway);

        this.dataService.updateGateway(updatedGateway).subscribe(
          response => {
            console.log(`Gateway tenant updated successfully:`, response);
            this.toast.show(
              'Success',
              `Gateway "${gateway.name}" tenant updated successfully`,
              'success'
            );
          },
          error => {
            console.error(`Error updating gateway tenant:`, error);
            this.toast.show(
              'Error',
              `Failed to update gateway "${gateway.name}" tenant`,
              'danger'
            );
            if (this.gridApi) {
              this.gridApi.refreshCells({
                rowNodes: [this.gridApi.getRowNode(gateway._id)],
              });
            }
          }
        );
      } else {
        console.log('No tenant update needed - same value');
      }
    } else {
      const trimmedValue = newValue?.trim();
      const oldTrimmedValue = oldValue?.trim() || '';

      console.log('Field update check:', {
        fieldName,
        trimmedValue,
        oldValue,
        oldTrimmedValue,
        valuesMatch: trimmedValue === oldTrimmedValue,
      });

      // Update if value is not empty and different from old value
      // Note: ag-grid already updates the data model, so we compare against oldValue
      if (trimmedValue && trimmedValue !== oldTrimmedValue) {
        const updatedGateway = { ...gateway };
        this.setFieldValue(updatedGateway, fieldName, trimmedValue);
        console.log('Updating gateway with:', updatedGateway);

        this.dataService.updateGateway(updatedGateway).subscribe(
          response => {
            console.log(`Gateway ${fieldName} updated successfully:`, response);
            // Value is already updated in the data model by ag-grid, so we don't need to set it again
            this.toast.show(
              'Success',
              `Gateway "${gateway.name}" ${this.getFieldDisplayName(
                fieldName
              )} updated successfully`,
              'success'
            );
          },
          error => {
            console.error(`Error updating gateway ${fieldName}:`, error);
            // Revert the value on error
            if (oldTrimmedValue !== undefined) {
              this.setFieldValue(gateway, fieldName, oldTrimmedValue);
            }
            this.toast.show(
              'Error',
              `Failed to update gateway "${
                gateway.name
              }" ${this.getFieldDisplayName(fieldName)}`,
              'danger'
            );
            if (this.gridApi) {
              this.gridApi.refreshCells({
                rowNodes: [this.gridApi.getRowNode(gateway._id)],
              });
            }
          }
        );
      } else {
        console.log('No update needed - same value or empty value');
      }
    }
  }

  private getFieldValue(obj: any, fieldPath: string): any {
    return fieldPath.split('.').reduce((current, key) => current?.[key], obj);
  }

  private setFieldValue(obj: any, fieldPath: string, value: any): void {
    const keys = fieldPath.split('.');
    const lastKey = keys.pop();
    const target = keys.reduce((current, key) => {
      if (!current[key]) current[key] = {};
      return current[key];
    }, obj);

    if (lastKey) {
      target[lastKey] = value;
    }
  }

  private getFieldDisplayName(fieldName: string): string {
    const fieldDisplayNames: { [key: string]: string } = {
      name: 'name',
      serial: 'serial number',
      'attributes.eui': 'EUI',
      'tenant.tenantId': 'tenant',
    };
    return fieldDisplayNames[fieldName] || fieldName;
  }
}
